<div class="main-container ac-table-page">
  <header class="title">
    <span class="title-text">Reports</span>
    <p class="small-title">Pick an utsav, then a report.</p>
  </header>

  <div class="search-bar">
    <div class="each-div">
      <label class="drop-title" id="report-utsav-label">Utsav</label>
      <mat-form-field
        class="ac-select-field"
        appearance="outline"
        subscriptSizing="dynamic"
      >
        <mat-select
          aria-labelledby="report-utsav-label"
          placeholder="Select an utsav"
          panelClass="ac-select-panel"
          [disableOptionCentering]="true"
          [disabled]="isLoading || utsavs.length === 0"
          [formControl]="utsav"
          (selectionChange)="loadReport()"
        >
          @for(item of utsavs; track item.utsavId){
          <mat-option [value]="item.utsavId">{{ item.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
  </div>

  <div class="tabs-row">
    <div class="ac-tabs">
      @for(tab of reportTabs; track tab.key){
      <button type="button" class="ac-tab" [class.active]="activeReport === tab.key" (click)="setReport(tab.key)">
        {{ tab.label }}
      </button>
      }
    </div>
    <button type="button" class="ac-secondary-btn export-btn" [disabled]="isLoading || !report" (click)="exportCsv()">Export CSV</button>
  </div>

  @if(hasLoadedUtsavs && !isLoading && utsavsLoadFailed){
  <div class="empty-state">Utsavs could not be loaded. Please try again later.</div>
  } @else if(hasLoadedUtsavs && !isLoading && utsavs.length === 0){
  <div class="empty-state">No utsavs are available for this branch.</div>
  } @else if(!hasSelected && !isLoading){
  <div class="empty-state">Select an utsav to view the {{ activeReportLabel | lowercase }} report.</div>
  }

  @if(hasSelected && !isLoading && !report){
  <div class="empty-state">
    {{ reportLoadFailed ? 'The report could not be loaded. Please try again.' : 'No data is available for the selected utsav.' }}
  </div>
  }

  <!-- ===== Occupancy (target vs assigned) ===== -->
  @if(hasSelected && report && activeReport === 'occupancy'){
  <div class="report-layout">
    @if(report.target){
    <div class="report-table-container ac-table-region" role="region" aria-label="Utsav occupancy by capacity type" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead>
          <tr>
            <th scope="col" class="bucket-col">Capacity type</th>
            <th scope="col" class="num-col">Target</th>
            <th scope="col" class="num-col">Assigned</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="bucket-col" data-label="Capacity type">Individual (dormitory)</td>
            <td class="num-col" data-label="Target">{{ report.target.individualCapacity ?? 0 }}</td>
            <td class="num-col" data-label="Assigned">{{ report.assigned?.individual ?? 0 }}</td>
          </tr>
          <tr>
            <td class="bucket-col" data-label="Capacity type">Family (room)</td>
            <td class="num-col" data-label="Target">{{ report.target.familyCapacity ?? 0 }}</td>
            <td class="num-col" data-label="Assigned">{{ report.assigned?.family ?? 0 }}</td>
          </tr>
          <tr>
            <td class="bucket-col" data-label="Capacity type">Bulk (hall)</td>
            <td class="num-col" data-label="Target">{{ report.target.bulkCapacity ?? 0 }}</td>
            <td class="num-col" data-label="Assigned">{{ report.assigned?.bulk ?? 0 }}</td>
          </tr>
          <tr class="total-row">
            <td class="bucket-col" data-label="Capacity type">Total</td>
            <td class="num-col" data-label="Target">{{ report.target.totalCapacity ?? 0 }}</td>
            <td class="num-col" data-label="Assigned">{{ report.assigned?.total ?? 0 }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    } @else {
    <div class="empty-state report-empty">No inventory target is set for this utsav.</div>
    }

    <div class="count-card">
      <span class="count-label">Occupied beds</span>
      <span class="count-value">{{ report.occupiedBeds ?? 0 }}</span>
    </div>
  </div>
  }

  <!-- ===== Booking summary (funnel + guests) ===== -->
  @if(hasSelected && report && activeReport === 'summary'){
  <div class="stat-grid">
    <div class="count-card"><span class="count-label">Pending (unassigned)</span><span class="count-value">{{ report.bookings?.pending ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Assigned</span><span class="count-value">{{ report.bookings?.assigned ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Confirmed</span><span class="count-value">{{ report.bookings?.confirmed ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Checked-in</span><span class="count-value">{{ report.bookings?.checkedIn ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Checked-out</span><span class="count-value">{{ report.bookings?.checkedOut ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Cancelled</span><span class="count-value">{{ report.bookings?.cancelled ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Rejected</span><span class="count-value">{{ report.bookings?.rejected ?? 0 }}</span></div>
    <div class="count-card total"><span class="count-label">Total bookings</span><span class="count-value">{{ report.bookings?.total ?? 0 }}</span></div>
  </div>

  <h2 class="section-title">Guests (live bookings)</h2>
  <div class="stat-grid">
    <div class="count-card total"><span class="count-label">Total guests</span><span class="count-value">{{ report.guests?.total ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Male</span><span class="count-value">{{ report.guests?.male ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Female</span><span class="count-value">{{ report.guests?.female ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Children</span><span class="count-value">{{ report.guests?.children ?? 0 }}</span></div>
  </div>
  <p class="report-note">Guest totals exclude cancelled and rejected bookings.</p>
  }

  <!-- ===== Occupancy timeline (beds per day) ===== -->
  @if(hasSelected && report && activeReport === 'timeline'){
  <div class="stat-grid">
    <div class="count-card total"><span class="count-label">Assigned capacity</span><span class="count-value">{{ report.capacity?.total ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Dormitory beds</span><span class="count-value">{{ report.capacity?.DORMITORY ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Room beds</span><span class="count-value">{{ report.capacity?.ROOM ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Hall beds</span><span class="count-value">{{ report.capacity?.HALL ?? 0 }}</span></div>
  </div>
  <p class="report-note">
    @if(report.startDate){ {{ report.startDate }} to {{ report.endDate }} · }
    beds blocked per day by live bookings (a booking blocks its beds for the whole stay; checked-out stays count for their dates).
  </p>

  <div class="report-table-container ac-table-region" role="region" aria-label="Occupancy per day" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col" class="num-col">Occupied</th>
          <th scope="col" class="num-col">Capacity</th>
          <th scope="col" class="pct-col">Occupancy</th>
          <th scope="col" class="num-col">Dormitory</th>
          <th scope="col" class="num-col">Room</th>
          <th scope="col" class="num-col">Hall</th>
        </tr>
      </thead>
      <tbody>
        @for(day of report.days; track day.date){
        <tr>
          <td data-label="Date">{{ day.date }}</td>
          <td class="num-col" data-label="Occupied">{{ day.occupied }}</td>
          <td class="num-col" data-label="Capacity">{{ report.capacity?.total ?? 0 }}</td>
          <td class="pct-col" data-label="Occupancy">
            <div class="pct-cell">
              <div class="pct-bar"><div class="pct-fill" [class.full]="day.pct >= 100" [style.width.%]="day.pct"></div></div>
              <span>{{ day.pct }}%</span>
            </div>
          </td>
          <td class="num-col" data-label="Dormitory">{{ day.DORMITORY }}</td>
          <td class="num-col" data-label="Room">{{ day.ROOM }}</td>
          <td class="num-col" data-label="Hall">{{ day.HALL }}</td>
        </tr>
        } @empty {
        <tr><td colspan="7" class="empty-row">No accommodation window or bookings to show.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== Arrivals & departures (per day) ===== -->
  @if(hasSelected && report && activeReport === 'movements'){
  <div class="stat-grid">
    <div class="count-card"><span class="count-label">Expected arrivals</span><span class="count-value">{{ report.totals?.arrivals ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Arrived so far</span><span class="count-value">{{ report.totals?.arrived ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Expected departures</span><span class="count-value">{{ report.totals?.departures ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Departed so far</span><span class="count-value">{{ report.totals?.departed ?? 0 }}</span></div>
  </div>
  <p class="report-note">
    @if(report.startDate){ {{ report.startDate }} to {{ report.endDate }} · }
    live bookings by from-date (arrivals) and to-date (departures); "arrived / departed" are the actual check-ins / check-outs so far. Today: {{ report.today }}.
  </p>

  <div class="report-table-container ac-table-region" role="region" aria-label="Arrivals and departures per day" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col" class="num-col">Arrivals</th>
          <th scope="col" class="num-col">Arriving guests</th>
          <th scope="col" class="num-col">Arrived</th>
          <th scope="col" class="num-col">Departures</th>
          <th scope="col" class="num-col">Departing guests</th>
          <th scope="col" class="num-col">Departed</th>
        </tr>
      </thead>
      <tbody>
        @for(day of report.days; track day.date){
        <tr [class.today-row]="day.date === report.today">
          <td data-label="Date">{{ day.date }}@if(day.date === report.today){ <span class="muted">(today)</span> }</td>
          <td class="num-col" data-label="Arrivals">{{ day.arrivals }}</td>
          <td class="num-col" data-label="Arriving guests">{{ day.arrivalGuests }}</td>
          <td class="num-col" data-label="Arrived">{{ day.arrived }}</td>
          <td class="num-col" data-label="Departures">{{ day.departures }}</td>
          <td class="num-col" data-label="Departing guests">{{ day.departureGuests }}</td>
          <td class="num-col" data-label="Departed">{{ day.departed }}</td>
        </tr>
        } @empty {
        <tr><td colspan="7" class="empty-row">No accommodation window or bookings to show.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== Utilization (per property, drill into rooms) ===== -->
  @if(hasSelected && report && activeReport === 'utilization'){
  <div class="stat-grid">
    <div class="count-card"><span class="count-label">Rooms in utsav</span><span class="count-value">{{ report.totals?.roomsInUtsav ?? 0 }} / {{ report.totals?.totalRooms ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Capacity in utsav</span><span class="count-value">{{ report.totals?.capacityInUtsav ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Beds assigned</span><span class="count-value">{{ report.totals?.bedsAssigned ?? 0 }}</span></div>
    <div class="count-card total"><span class="count-label">Utilization</span><span class="count-value">{{ report.totals?.utilizationPct ?? 0 }}%</span></div>
    <div class="count-card"><span class="count-label">Active rooms not in utsav</span><span class="count-value">{{ report.totals?.unassignedActiveRooms ?? 0 }}</span></div>
  </div>

  <div class="report-table-container ac-table-region" role="region" aria-label="Utilization by property" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col" class="bucket-col">Property</th>
          <th scope="col" class="num-col">Rooms</th>
          <th scope="col" class="num-col">In utsav</th>
          <th scope="col" class="num-col">Capacity</th>
          <th scope="col" class="num-col">Beds assigned</th>
          <th scope="col" class="num-col">Utilization</th>
          <th scope="col" class="num-col">Not in utsav</th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        @for(p of report.properties; track p.propertyId){
        <tr [class.inactive]="p.propertyStatus !== 1">
          <td class="bucket-col" data-label="Property">{{ p.propertyName }}@if(p.propertyStatus !== 1){ <span class="muted">(inactive)</span> }</td>
          <td class="num-col" data-label="Rooms">{{ p.activeRooms }} / {{ p.totalRooms }}</td>
          <td class="num-col" data-label="In utsav">{{ p.roomsInUtsav }}</td>
          <td class="num-col" data-label="Capacity">{{ p.capacityInUtsav }}</td>
          <td class="num-col" data-label="Beds assigned">{{ p.bedsAssigned }}</td>
          <td class="num-col" data-label="Utilization">{{ p.utilizationPct }}%</td>
          <td class="num-col" data-label="Not in utsav">{{ p.unassignedActiveRooms }}</td>
          <td data-label="Rooms detail">
            <button type="button" class="ac-secondary-small-btn rooms-btn" (click)="toggleProperty(p.propertyId)">
              {{ expandedPropertyId === p.propertyId ? 'Hide rooms' : 'View rooms' }}
            </button>
          </td>
        </tr>
        @if(expandedPropertyId === p.propertyId){
        <tr class="ac-detail-row">
          <td colspan="8">
            <div class="rooms-panel">
              @for(room of p.rooms; track room.roomId){
              <div class="room-line" [class.dim]="!room.active">
                <span class="room-info">Room {{ room.roomNumber }} · {{ room.roomType }} · Capacity {{ room.capacity }}</span>
                <span class="room-flag">{{ room.active ? (room.inUtsav ? 'In utsav' : 'Not in utsav') : 'Inactive' }}</span>
                <span class="room-beds">{{ room.bedsAssigned }} / {{ room.capacity }} beds</span>
              </div>
              } @empty {
              <div class="muted">No rooms in this property.</div>
              }
            </div>
          </td>
        </tr>
        }
        } @empty {
        <tr><td colspan="8" class="empty-row">No properties in this branch.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== Demand vs supply ===== -->
  @if(hasSelected && report && activeReport === 'demandSupply'){
  <div class="stat-grid">
    <div class="count-card total"><span class="count-label">Assigned capacity</span><span class="count-value">{{ report.supply?.total ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Dormitory beds</span><span class="count-value">{{ report.supply?.DORMITORY ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Room beds</span><span class="count-value">{{ report.supply?.ROOM ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Hall beds</span><span class="count-value">{{ report.supply?.HALL ?? 0 }}</span></div>
  </div>
  <p class="report-note">
    Demand is grouped by booking size — 1 guest = Individual (dormitory), 2–4 = Family (room), 5+ = Bulk (hall) — because bookings
    carry a single accommodation type. "Mismatched" counts beds placed outside the natural room type for that size.
  </p>

  <div class="report-table-container ac-table-region" role="region" aria-label="Demand vs supply" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col" class="bucket-col">Demand (by size)</th>
          <th scope="col" class="num-col">Bookings</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col" class="num-col">Unassigned bookings</th>
          <th scope="col" class="num-col">Unassigned guests</th>
          <th scope="col" class="num-col">Beds in dormitory</th>
          <th scope="col" class="num-col">Beds in room</th>
          <th scope="col" class="num-col">Beds in hall</th>
          <th scope="col" class="num-col">Mismatched</th>
        </tr>
      </thead>
      <tbody>
        @for(d of report.demand; track d.bucket){
        <tr>
          <td class="bucket-col" data-label="Demand">{{ d.bucket | titlecase }} <span class="muted">→ {{ d.naturalRoomType | titlecase }}</span></td>
          <td class="num-col" data-label="Bookings">{{ d.bookings }}</td>
          <td class="num-col" data-label="Guests">{{ d.guests }}</td>
          <td class="num-col" data-label="Unassigned bookings">{{ d.unassignedBookings }}</td>
          <td class="num-col" data-label="Unassigned guests">{{ d.unassignedGuests }}</td>
          <td class="num-col" data-label="Beds in dormitory">{{ d.beds?.DORMITORY ?? 0 }}</td>
          <td class="num-col" data-label="Beds in room">{{ d.beds?.ROOM ?? 0 }}</td>
          <td class="num-col" data-label="Beds in hall">{{ d.beds?.HALL ?? 0 }}</td>
          <td class="num-col" data-label="Mismatched" [class.warn]="d.mismatchedBeds > 0">{{ d.mismatchedBeds }}</td>
        </tr>
        }
      </tbody>
    </table>
  </div>

  @if(report.requestedTypes?.length){
  <h2 class="section-title">Recorded booking / accommodation types</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Recorded types" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Booking type</th>
          <th scope="col">Accommodation type</th>
          <th scope="col" class="num-col">Bookings</th>
        </tr>
      </thead>
      <tbody>
        @for(t of report.requestedTypes; track $index){
        <tr>
          <td data-label="Booking type">{{ t.bookingType || '-' }}</td>
          <td data-label="Accommodation type">{{ t.accommodationType || '-' }}</td>
          <td class="num-col" data-label="Bookings">{{ t.bookings }}</td>
        </tr>
        }
      </tbody>
    </table>
  </div>
  }
  }

  <!-- ===== Cancellations & rejections ===== -->
  @if(hasSelected && report && activeReport === 'cancellations'){
  <div class="stat-grid">
    <div class="count-card"><span class="count-label">Cancelled</span><span class="count-value">{{ report.totals?.cancelled ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Rejected</span><span class="count-value">{{ report.totals?.rejected ?? 0 }}</span></div>
    <div class="count-card" [class.total]="(report.totals?.holdingBeds ?? 0) > 0">
      <span class="count-label">Still holding beds</span>
      <span class="count-value">{{ report.totals?.holdingBeds ?? 0 }}</span>
    </div>
  </div>
  <p class="report-note">
    "Changed on" is the day the booking was last updated (the status change). Bookings still holding beds block rooms — revoke them
    from the Bookings screen (Cancelled / Rejected view → Revoke rooms).
  </p>

  @if(report.byDay?.length){
  <h2 class="section-title">Per day</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Cancellations per day" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col" class="num-col">Cancelled</th>
          <th scope="col" class="num-col">Rejected</th>
        </tr>
      </thead>
      <tbody>
        @for(d of report.byDay; track d.date){
        <tr>
          <td data-label="Date">{{ d.date }}</td>
          <td class="num-col" data-label="Cancelled">{{ d.cancelled }}</td>
          <td class="num-col" data-label="Rejected">{{ d.rejected }}</td>
        </tr>
        }
      </tbody>
    </table>
  </div>
  }

  <h2 class="section-title">Bookings — {{ report.bookings?.length ?? 0 }}</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Cancelled and rejected bookings" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Booking ID</th>
          <th scope="col">Family Code</th>
          <th scope="col">Status</th>
          <th scope="col">Changed on</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col">From</th>
          <th scope="col">To</th>
          <th scope="col">Reason</th>
          <th scope="col" class="num-col">Beds held</th>
        </tr>
      </thead>
      <tbody>
        @for(b of report.bookings; track b.bookingId){
        <tr [class.warn-row]="b.heldBeds > 0">
          <td data-label="Booking ID">{{ b.bookingId }}</td>
          <td data-label="Family Code">{{ b.familyCode }}</td>
          <td data-label="Status">{{ b.statusLabel }}</td>
          <td data-label="Changed on">{{ b.changedOn || '-' }}</td>
          <td class="num-col" data-label="Guests">{{ b.guestCount }}</td>
          <td data-label="From">{{ b.fromDate }}</td>
          <td data-label="To">{{ b.toDate }}</td>
          <td data-label="Reason" class="reason-col">{{ b.reason || '-' }}</td>
          <td class="num-col" data-label="Beds held">{{ b.heldBeds }}</td>
        </tr>
        } @empty {
        <tr><td colspan="9" class="empty-row">No cancellations or rejections.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== Booking velocity ===== -->
  @if(hasSelected && report && activeReport === 'velocity'){
  <div class="stat-grid">
    <div class="count-card total"><span class="count-label">Bookings created</span><span class="count-value">{{ report.totals?.bookings ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Still live</span><span class="count-value">{{ report.totals?.live ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Guests booked</span><span class="count-value">{{ report.totals?.guests ?? 0 }}</span></div>
  </div>
  <p class="report-note">
    @if(report.startDate){ {{ report.startDate }} to {{ report.endDate }} · }
    bookings by the day they were created (the utsav booking window, widened if bookings fall outside it). "Live" excludes cancelled / rejected.
  </p>

  <div class="report-table-container ac-table-region" role="region" aria-label="Bookings created per day" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col" class="num-col">Bookings</th>
          <th scope="col" class="num-col">Live</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col" class="num-col">Cumulative bookings</th>
          <th scope="col" class="num-col">Cumulative guests</th>
        </tr>
      </thead>
      <tbody>
        @for(day of report.days; track day.date){
        <tr [class.quiet-row]="day.bookings === 0">
          <td data-label="Date">{{ day.date }}</td>
          <td class="num-col" data-label="Bookings">{{ day.bookings }}</td>
          <td class="num-col" data-label="Live">{{ day.live }}</td>
          <td class="num-col" data-label="Guests">{{ day.guests }}</td>
          <td class="num-col" data-label="Cumulative bookings">{{ day.cumulativeBookings }}</td>
          <td class="num-col" data-label="Cumulative guests">{{ day.cumulativeGuests }}</td>
        </tr>
        } @empty {
        <tr><td colspan="6" class="empty-row">No bookings yet for this utsav.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== Guest composition ===== -->
  @if(hasSelected && report && activeReport === 'guests'){
  <p class="report-note">Live bookings only. Gender comes from the devotee profile for family members and from the co-visitor entry for guests; "Unknown" means it was never filled in. The Booking summary has the booking-level M / F / C split.</p>

  <div class="composition-grid">
    <div class="report-table-container ac-table-region" role="region" aria-label="Guests by type" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Guest type</th><th scope="col" class="num-col">Guests</th></tr></thead>
        <tbody>
          @for(g of report.guestsByType; track g.guestType){
          <tr><td data-label="Guest type">{{ g.guestType | titlecase }}</td><td class="num-col" data-label="Guests">{{ g.guests }}</td></tr>
          } @empty { <tr><td colspan="2" class="empty-row">No guest records.</td></tr> }
        </tbody>
      </table>
    </div>

    <div class="report-table-container ac-table-region" role="region" aria-label="Guests by gender" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Gender</th><th scope="col" class="num-col">Guests</th></tr></thead>
        <tbody>
          @for(g of report.guestsByGender; track g.gender){
          <tr><td data-label="Gender">{{ g.gender | titlecase }}</td><td class="num-col" data-label="Guests">{{ g.guests }}</td></tr>
          } @empty { <tr><td colspan="2" class="empty-row">No guest records.</td></tr> }
        </tbody>
      </table>
    </div>

    <div class="report-table-container ac-table-region" role="region" aria-label="Bookings by size" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Guests per booking</th><th scope="col" class="num-col">Bookings</th><th scope="col" class="num-col">Guests</th></tr></thead>
        <tbody>
          @for(b of report.bookingsBySize; track b.size){
          <tr><td data-label="Guests per booking">{{ b.size }}</td><td class="num-col" data-label="Bookings">{{ b.bookings }}</td><td class="num-col" data-label="Guests">{{ b.guests }}</td></tr>
          }
        </tbody>
      </table>
    </div>

    <div class="report-table-container ac-table-region" role="region" aria-label="Bookings by type" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Booking type</th><th scope="col" class="num-col">Bookings</th><th scope="col" class="num-col">Guests</th></tr></thead>
        <tbody>
          @for(b of report.bookingsByType; track b.bookingType){
          <tr><td data-label="Booking type">{{ b.bookingType }}</td><td class="num-col" data-label="Bookings">{{ b.bookings }}</td><td class="num-col" data-label="Guests">{{ b.guests }}</td></tr>
          } @empty { <tr><td colspan="3" class="empty-row">No bookings.</td></tr> }
        </tbody>
      </table>
    </div>
  </div>
  }

  <!-- ===== Front-desk activity ===== -->
  @if(hasSelected && report && activeReport === 'frontdesk'){
  <div class="stat-grid">
    <div class="count-card total"><span class="count-label">Check-ins</span><span class="count-value">{{ report.totals?.checkins ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Check-outs</span><span class="count-value">{{ report.totals?.checkouts ?? 0 }}</span></div>
    <div class="count-card"><span class="count-label">Avg arrival delay</span><span class="count-value">{{ report.totals?.avgDelayDays ?? '—' }}<span class="count-unit"> days</span></span></div>
    <div class="count-card"><span class="count-label">On time</span><span class="count-value">{{ report.totals?.onTime ?? 0 }}</span></div>
    <div class="count-card" [class.total]="(report.totals?.late ?? 0) > 0"><span class="count-label">Late arrivals</span><span class="count-value">{{ report.totals?.late ?? 0 }}</span></div>
  </div>
  <p class="report-note">Times are IST. Arrival delay = check-in date minus the booked from-date (0 or negative = on time). Only check-ins / check-outs made after the stamps were introduced are counted.</p>

  <div class="composition-grid">
    <div class="report-table-container ac-table-region" role="region" aria-label="Activity by operator" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Operator</th><th scope="col" class="num-col">Check-ins</th><th scope="col" class="num-col">Check-outs</th></tr></thead>
        <tbody>
          @for(o of report.byOperator; track o.operator){
          <tr><td data-label="Operator">{{ o.operator }}</td><td class="num-col" data-label="Check-ins">{{ o.checkins }}</td><td class="num-col" data-label="Check-outs">{{ o.checkouts }}</td></tr>
          } @empty { <tr><td colspan="3" class="empty-row">No front-desk activity yet.</td></tr> }
        </tbody>
      </table>
    </div>

    <div class="report-table-container ac-table-region" role="region" aria-label="Activity per day" tabindex="0">
      <table class="occupancy-table ac-responsive-table">
        <thead><tr><th scope="col">Date</th><th scope="col" class="num-col">Check-ins</th><th scope="col" class="num-col">Check-outs</th></tr></thead>
        <tbody>
          @for(d of report.byDay; track d.date){
          <tr><td data-label="Date">{{ d.date }}</td><td class="num-col" data-label="Check-ins">{{ d.checkins }}</td><td class="num-col" data-label="Check-outs">{{ d.checkouts }}</td></tr>
          } @empty { <tr><td colspan="3" class="empty-row">No front-desk activity yet.</td></tr> }
        </tbody>
      </table>
    </div>
  </div>

  <h2 class="section-title">Latest activity</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Latest front-desk events" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">When (IST)</th>
          <th scope="col">Event</th>
          <th scope="col">Booking ID</th>
          <th scope="col">Family Code</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col">Operator</th>
        </tr>
      </thead>
      <tbody>
        @for(e of report.recent; track $index){
        <tr>
          <td data-label="When">{{ e.at }}</td>
          <td data-label="Event">{{ e.event }}</td>
          <td data-label="Booking ID">{{ e.bookingId }}</td>
          <td data-label="Family Code">{{ e.familyCode }}</td>
          <td class="num-col" data-label="Guests">{{ e.guestCount }}</td>
          <td data-label="Operator">{{ e.operator || '-' }}</td>
        </tr>
        } @empty {
        <tr><td colspan="6" class="empty-row">No check-ins or check-outs recorded yet.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }

  <!-- ===== No-shows / overdue ===== -->
  @if(hasSelected && report && activeReport === 'noShows'){
  <p class="report-note">As of {{ report.today }}. Overdue check-ins are confirmed, assigned bookings whose from-date has passed; overdue check-outs are checked-in bookings whose to-date has passed.</p>

  <h2 class="section-title">Overdue check-ins (no-shows) — {{ report.overdueCheckIns?.length ?? 0 }}</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Overdue check-ins" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Booking ID</th>
          <th scope="col">Family Code</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col">From</th>
          <th scope="col">To</th>
          <th scope="col">Rooms</th>
        </tr>
      </thead>
      <tbody>
        @for(b of report.overdueCheckIns; track b.bookingId){
        <tr>
          <td data-label="Booking ID">{{ b.bookingId }}</td>
          <td data-label="Family Code">{{ b.familyCode }}</td>
          <td class="num-col" data-label="Guests">{{ b.guestCount }}</td>
          <td data-label="From">{{ b.fromDate }}</td>
          <td data-label="To">{{ b.toDate }}</td>
          <td data-label="Rooms">{{ b.rooms || '-' }}</td>
        </tr>
        } @empty {
        <tr><td colspan="6" class="empty-row">No overdue check-ins.</td></tr>
        }
      </tbody>
    </table>
  </div>

  <h2 class="section-title">Overdue check-outs — {{ report.overdueCheckOuts?.length ?? 0 }}</h2>
  <div class="report-table-container ac-table-region" role="region" aria-label="Overdue check-outs" tabindex="0">
    <table class="occupancy-table ac-responsive-table">
      <thead>
        <tr>
          <th scope="col">Booking ID</th>
          <th scope="col">Family Code</th>
          <th scope="col" class="num-col">Guests</th>
          <th scope="col">From</th>
          <th scope="col">To</th>
          <th scope="col">Rooms</th>
        </tr>
      </thead>
      <tbody>
        @for(b of report.overdueCheckOuts; track b.bookingId){
        <tr>
          <td data-label="Booking ID">{{ b.bookingId }}</td>
          <td data-label="Family Code">{{ b.familyCode }}</td>
          <td class="num-col" data-label="Guests">{{ b.guestCount }}</td>
          <td data-label="From">{{ b.fromDate }}</td>
          <td data-label="To">{{ b.toDate }}</td>
          <td data-label="Rooms">{{ b.rooms || '-' }}</td>
        </tr>
        } @empty {
        <tr><td colspan="6" class="empty-row">No overdue check-outs.</td></tr>
        }
      </tbody>
    </table>
  </div>
  }
</div>

<app-loader [isLoading]="isLoading"></app-loader>
